/**
 * Billing API client and helpers for the plan UI. The backend owns the plan
 * catalog (Cerebrex/utils/plans.js). This file only reads GET /api/billing.
 */
import { useCallback, useEffect, useState } from 'react';
import api from './api';

export type FeatureKey =
  | 'customBranding'
  | 'docxImport'
  | 'customDomain'
  | 'analytics'
  | 'aiDrafts'
  | 'tvScoreboard'
  | 'seasons'
  | 'multiHost';

export type LimitKey = 'venues' | 'gameNightsPerMonth' | 'aiCreditsPerMonth';

export interface PlanSummary {
  key: string;
  name: string;
  priceMonthlyUsd: number;
  limits: Record<LimitKey, number | null>;
  features: FeatureKey[];
  purchasable: boolean;
}

export interface BillingInfo {
  billingEnabled: boolean;
  plan: {
    key: string;
    name: string;
    status: string | null;
    trialEndsAt: string | null;
    trialDaysLeft: number;
  };
  limits: Record<LimitKey, number | null>;
  // null when usage is not tracked yet.
  usage: Record<LimitKey, number | null>;
  features: Record<FeatureKey, boolean>;
  // AI credits bought in packs. They do not expire.
  aiCreditBalance: number;
  creditPack: { credits: number; priceUsd: number; purchasable: boolean };
  hasBillingAccount: boolean;
  hasSubscription: boolean;
  canManage: boolean;
  plans: PlanSummary[];
}

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  customBranding: 'Custom branding',
  docxImport: '.docx import',
  customDomain: 'Custom public domain',
  analytics: 'Analytics',
  aiDrafts: 'AI question drafts',
  tvScoreboard: 'TV scoreboard',
  seasons: 'Season standings',
  multiHost: 'Multiple hosts and guest roles',
};

export const LIMIT_LABELS: Record<LimitKey, string> = {
  venues: 'Venues',
  gameNightsPerMonth: 'Game nights this month',
  aiCreditsPerMonth: 'AI credits this month',
};

export async function fetchBilling(): Promise<BillingInfo> {
  const res = await api.get('/api/billing');
  return res.data.data as BillingInfo;
}

// Both return the Checkout Session client secret for the embedded form.
export async function startCheckout(plan: string): Promise<string> {
  const res = await api.post('/api/billing/checkout', { plan });
  return res.data.data.clientSecret as string;
}

export async function startCreditPackCheckout(): Promise<string> {
  const res = await api.post('/api/billing/credits');
  return res.data.data.clientSecret as string;
}

export async function openPortal(): Promise<string> {
  const res = await api.post('/api/billing/portal');
  return res.data.data.url as string;
}

// Load the billing info once. `billing` stays null on an error, so callers
// render nothing instead of a broken banner.
export function useBilling(): { billing: BillingInfo | null; loading: boolean; reload: () => void } {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    fetchBilling()
      .then(setBilling)
      .catch(() => setBilling(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { billing, loading, reload: load };
}

export interface PlanLimitDetails {
  code: 'PLAN_LIMIT' | 'PLAN_FEATURE' | 'ORG_SUSPENDED';
  key: string;
  limit?: number;
  used?: number;
  plan?: string;
  message: string;
}

// Read a 402 plan error from an axios error. Returns null for any other error.
export function planLimitFromError(err: unknown): PlanLimitDetails | null {
  const e = err as { response?: { status?: number; data?: { message?: string; details?: Partial<PlanLimitDetails> } } };
  const details = e?.response?.data?.details;
  if (e?.response?.status !== 402 || !details?.code) return null;
  if (!['PLAN_LIMIT', 'PLAN_FEATURE', 'ORG_SUSPENDED'].includes(details.code)) return null;
  return {
    code: details.code,
    key: String(details.key ?? ''),
    limit: details.limit,
    used: details.used,
    plan: details.plan,
    message: e.response?.data?.message || 'Your plan does not allow this. Upgrade to continue.',
  };
}

// "2 of 3", or "2" when there is no limit.
export function usageLabel(used: number, limit: number | null): string {
  return limit === null ? `${used} (unlimited)` : `${used} of ${limit}`;
}

// 0 to 100 for a usage meter. 0 when there is no limit.
export function usagePercent(used: number, limit: number | null): number {
  if (limit === null || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}
